import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useAuth, API } from '../App';
import { toast } from 'sonner';
import axios from 'axios';
import Logo from '../components/Logo';
import {
  Calculator,
  Building2,
  Users,
  MessageSquare,
  FileText,
  Settings,
  LogOut,
  TrendingUp,
  BarChart3,
  Wallet,
  Crown,
  Bot,
  Sparkles,
  Mic,
  Map,
  Zap,
  Calendar,
  ArrowRight,
  MapPin,
  Briefcase,
  Shield,
  Clock,
  Headphones,
  Radio,
  Play,
  PartyPopper,
  Star,
  AlertTriangle,
  Activity,
  BookOpen,
  BellRing
} from 'lucide-react';
import { isNativePlatform, hasFullAccess } from '../lib/platform';
import AppleIAPButton from '../components/AppleIAPButton';
import LaunchCampaignPanel from '../components/LaunchCampaignPanel';
import ClubMembersPanel from '../components/ClubMembersPanel';
import SoldPropertiesPanel from '../components/SoldPropertiesPanel';

const FOUNDER_EMAIL = "rajasalame@gmail.com";

const DashboardPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [stats, setStats] = useState({ calculations: 0, mdbProjects: 0, posts: 0 });
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [pendingPropertiesCount, setPendingPropertiesCount] = useState(0);

  const isFounder = user?.email === FOUNDER_EMAIL;

  useEffect(() => {
    // Check for payment success
    const sessionId = searchParams.get('session_id');
    const paymentStatus = searchParams.get('payment');

    if (sessionId && paymentStatus === 'success') {
      pollPaymentStatus(sessionId);
    }

    fetchStats();
    if (isFounder) {
      fetchPendingProperties();
    }
  }, [searchParams, isFounder]);

  const pollPaymentStatus = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/subscription/status/${sessionId}`, { withCredentials: true });
      if (response.data.payment_status === 'paid') {
        toast.success('Paiement réussi ! Votre abonnement est actif.');
        // Clear URL params
        navigate('/dashboard', { replace: true });
      }
    } catch (error) {
      console.error('Error checking payment:', error);
    }
  };

  const fetchPendingProperties = async () => {
    try {
      const response = await axios.get(`${API}/properties/admin/pending`, { withCredentials: true });
      setPendingPropertiesCount(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching pending properties:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const [calcs, projects, eventsRes] = await Promise.all([
        axios.get(`${API}/calculator/history`, { withCredentials: true }),
        axios.get(`${API}/mdb/projects`, { withCredentials: true }),
        axios.get(`${API}/events/dashboard/upcoming`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      setStats({
        calculations: calcs.data?.length || 0,
        mdbProjects: projects.data?.length || 0,
        posts: 0
      });
      setUpcomingEvents(eventsRes.data || []);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const _native = isNativePlatform();

  const menuItems = [
    { icon: Calculator, label: 'Calculateur', path: '/calculator', color: 'text-[#0D47A1]' },
    { icon: Building2, label: 'Outil MDB', path: '/mdb', color: 'text-[#00B050]' },
    { icon: Bot, label: 'Agent IA', path: '/ai-agent', color: 'text-purple-600', ...(!_native && { badge: 'Club' }) },
    { icon: Briefcase, label: 'Portfolio', path: '/portfolio', color: 'text-violet-600', ...(!_native && { badge: 'Club' }) },
    { icon: BellRing, label: 'Alertes', path: '/alerts', color: 'text-amber-500', ...(!_native && { badge: 'Club' }) },
    { icon: BarChart3, label: 'Stats IA', path: '/ai-stats', color: 'text-indigo-600', ...(!_native && { badge: 'Club' }) },
    { icon: MapPin, label: 'Cadastre DVF', path: '/cadastre', color: 'text-teal-600', ...(!_native && { badge: 'Club' }) },
    { icon: AlertTriangle, label: 'Diagnostic Climat', path: '/diagnostic-climatique', color: 'text-amber-600', ...(!_native && { badge: 'Club' }) },
    { icon: Users, label: 'Communauté', path: '/community', color: 'text-orange-500' },
    ...(isFounder ? [{ icon: Users, label: 'Utilisateurs potentiels', path: '/launch-guests', color: 'text-[#00B050]', isNew: true }] : []),
    { icon: BookOpen, label: 'Blog', path: '/blog', color: 'text-rose-500', ...(!_native && { badge: 'Club' }) },
    { icon: MessageSquare, label: 'Messages Privés', path: '/messages', color: 'text-violet-500', ...(!_native && { badge: 'Club' }) },
  ];

  const newFeatures = [
    { 
      icon: Building2, 
      label: 'Biens Immobiliers', 
      description: 'Publiez et découvrez des biens de la communauté',
      path: '/properties', 
      color: 'from-rose-500 to-pink-600',
      ...(!_native && { badge: 'Club' })
    },
    { 
      icon: Users, 
      label: _native ? 'Les Membres' : 'Les Membres du Club', 
      description: 'Trouvez des investisseurs près de chez vous',
      path: '/directory', 
      color: 'from-emerald-500 to-teal-600',
      ...(!_native && { badge: 'Club' }),
      hasLogo: true
    },
    { 
      icon: Briefcase, 
      label: 'Pros Partenaires', 
      description: 'Réseau de professionnels partenaires',
      path: '/pros-directory', 
      color: 'from-violet-500 to-purple-600',
      badge: 'Réseau',
      hasLogo: true
    },
    { 
      icon: Zap, 
      label: 'Intégrations', 
      description: 'Connectez Calendly et Zapier',
      path: '/integrations', 
      color: 'from-orange-500 to-amber-600',
      ...(!_native && { badge: 'Club' })
    },
  ];

  // Logo 10M Investor URL
  const logo10M = "https://customer-assets.emergentagent.com/job_4ed8f34b-1bc9-42f5-8577-4843189c4948/artifacts/jd7g0w5b_LOGO%2010MILLIONSINVESTOR%20blanc.png";

  const getSubscriptionLabel = (tier) => {
    if (tier === 'club' || tier === 'expert' || tier === 'founder') return '10M INVESTOR';
    return 'GRATUIT';
  };

  const quickStats = [
    { label: 'Calculs effectués', value: stats.calculations, icon: BarChart3, color: 'bg-[#0D47A1]/10 text-[#0D47A1]' },
    { label: 'Projets MDB', value: stats.mdbProjects, icon: Building2, color: 'bg-[#00B050]/10 text-[#00B050]' },
    { label: 'Abonnement', value: getSubscriptionLabel(user?.subscription_tier), icon: Crown, color: 'bg-purple-100 text-purple-600' },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top Navigation */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20 sm:h-24">
            <Link to="/dashboard" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              <img src="/logo-horizontal.png" alt="10 Millions Investor" className="h-16 sm:h-20 w-auto" data-testid="header-logo" />
            </Link>
            <div className="flex items-center space-x-4">
              <Link to="/profile">
                <Button variant="ghost" size="sm" data-testid="profile-btn">
                  <Settings className="h-4 w-4 mr-2" />
                  Profil
                </Button>
              </Link>
              <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn" className="text-slate-600 hover:text-red-600">
                <LogOut className="h-4 w-4 mr-2" />
                Déconnexion
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900">
            Bonjour, {user?.name || 'Investisseur'} 👋
          </h1>
          <p className="text-slate-600 mt-1">
            Bienvenue sur votre tableau de bord. Que souhaitez-vous faire aujourd'hui ?
          </p>
        </div>

        {/* Native PROMINENT subscription banner — visible BEFORE quick stats so App Review can locate IAP without scrolling */}
        {_native && user?.subscription_tier === 'free' && (
          <Link to="/abonnement" data-testid="native-top-subscribe-banner">
            <Card className="mb-6 border-0 bg-gradient-to-r from-[#0D47A1] to-[#1565C0] text-white shadow-lg hover:shadow-xl transition-shadow cursor-pointer">
              <CardContent className="p-5 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center flex-shrink-0">
                  <Crown className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-base">S'abonner au Club 10M Investor</h3>
                  <p className="text-blue-100 text-sm">Voir les offres d'abonnement (achat in-app)</p>
                </div>
                <ArrowRight className="h-5 w-5 text-white flex-shrink-0" />
              </CardContent>
            </Card>
          </Link>
        )}

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {quickStats.map((stat, index) => {
            const isSubscriptionCard = stat.label === 'Abonnement';
            const cardContent = (
              <Card key={index} className={`border-slate-200 ${isSubscriptionCard ? 'cursor-pointer hover:border-[#0D47A1] hover:shadow-lg transition-all' : ''}`}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">{stat.label}</p>
                      <p className="text-2xl font-bold text-slate-900 mt-1 font-mono">{stat.value}</p>
                    </div>
                    <div className={`w-12 h-12 rounded-lg ${stat.color} flex items-center justify-center`}>
                      <stat.icon className="h-6 w-6" />
                    </div>
                  </div>
                  {isSubscriptionCard && (
                    <p className="text-xs text-[#0D47A1] mt-2 font-medium">
                      {user?.subscription_tier === 'free' 
                        ? (_native ? 'Découvrir le Club →' : 'Cliquez pour voir les offres →')
                        : 'Gérer mon abonnement →'}
                    </p>
                  )}
                </CardContent>
              </Card>
            );

            if (isSubscriptionCard) {
              const subLink = _native ? '/abonnement' : (user?.subscription_tier === 'free' ? '/pricing' : '/profile');
              return (
                <Link key={index} to={subLink} data-testid="subscription-card-link">
                  {cardContent}
                </Link>
              );
            }
            return cardContent;
          })}
        </div>

        {/* Admin Section (Founder Only) */}
        {isFounder && (
          <>
          <Card className="mb-8 border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-amber-100 flex items-center justify-center">
                    <Shield className="h-7 w-7 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                      Administration Fondateur
                      {pendingPropertiesCount > 0 && (
                        <span className="bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full">
                          {pendingPropertiesCount} en attente
                        </span>
                      )}
                    </h3>
                    <p className="text-slate-600 text-sm">Surveillance et gestion de la plateforme</p>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Link to="/admin/analytics">
                  <Button className="w-full bg-slate-800 hover:bg-slate-900 text-white justify-start" data-testid="admin-analytics-btn">
                    <Activity className="h-4 w-4 mr-2" />
                    Analytics & Surveillance
                    <ArrowRight className="h-4 w-4 ml-auto" />
                  </Button>
                </Link>
                <Link to="/admin/properties">
                  <Button className="w-full bg-amber-500 hover:bg-amber-600 text-white justify-start" data-testid="admin-properties-btn">
                    <Clock className="h-4 w-4 mr-2" />
                    Valider les biens
                    {pendingPropertiesCount > 0 && (
                      <span className="ml-2 bg-white/20 px-2 py-0.5 rounded-full text-xs">
                        {pendingPropertiesCount}
                      </span>
                    )}
                    <ArrowRight className="h-4 w-4 ml-auto" />
                  </Button>
                </Link>
                <Link to="/admin/blog">
                  <Button className="w-full bg-[#0D47A1] hover:bg-[#0A367A] text-white justify-start" data-testid="admin-blog-btn">
                    <FileText className="h-4 w-4 mr-2" />
                    Gestion du Blog
                    <ArrowRight className="h-4 w-4 ml-auto" />
                  </Button>
                </Link>
                <Link to="/admin/tinypages">
                  <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white justify-start" data-testid="admin-tinypages-btn">
                    <Activity className="h-4 w-4 mr-2" />
                    Intégration TinyPages
                    <ArrowRight className="h-4 w-4 ml-auto" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* Launch Campaign Panel */}
          <LaunchCampaignPanel />

          {/* Club Members Panel */}
          <ClubMembersPanel />

          {/* Sold Properties Panel */}
          <SoldPropertiesPanel />
        </>
        )}

        {/* Upgrade Banner (for free users) */}
        {user?.subscription_tier === 'free' && (
          <Card className="mb-8 bg-gradient-to-r from-[#0D47A1] to-[#1976D2] text-white border-0">
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold">Passez à la version CLUB pour plus de fonctionnalités</h3>
                <p className="text-blue-100 mt-1">Dossier bancaire PDF, projets illimités, communauté et plus encore...</p>
              </div>
              {isNativePlatform() ? (
                <div className="ml-4 min-w-[200px]">
                  <AppleIAPButton compact onPurchaseSuccess={() => window.location.reload()} />
                </div>
              ) : (
                <Link to="/pricing">
                  <Button className="bg-white text-[#0D47A1] hover:bg-slate-100" data-testid="upgrade-btn">
                    <Crown className="h-4 w-4 mr-2" />
                    Rejoindre le Club
                  </Button>
                </Link>
              )}
            </CardContent>
          </Card>
        )}

        {/* Quick Actions Grid */}
        <h2 className="text-xl font-bold text-slate-900 mb-4">Accès rapide</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          {menuItems.filter(item => !item.isStudio).map((item, index) => (
            <Link key={index} to={item.path}>
              <Card className="border-slate-200 hover:border-[#0D47A1] transition-all hover:shadow-lg cursor-pointer h-full relative overflow-hidden">
                {item.badge && (
                  <div className="absolute top-2 right-2 bg-gradient-to-r from-[#00B050] to-[#0D47A1] text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                    <Sparkles className="h-3 w-3" />
                    {item.badge}
                  </div>
                )}
                {item.isNew && (
                  <div className="absolute top-2 right-2 bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                    <Sparkles className="h-3 w-3" />
                    Nouveau
                  </div>
                )}
                <CardContent className="p-6 flex flex-col items-center text-center">
                  <div className={`w-16 h-16 rounded-xl bg-slate-50 flex items-center justify-center mb-4`}>
                    <item.icon className={`h-8 w-8 ${item.color}`} />
                  </div>
                  <h3 className="font-semibold text-slate-900">{item.label}</h3>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {/* Studio Podcast Widget */}
        <div className="mb-6">
          <Link to="/classroom">
            <Card className="group relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border-0 hover:shadow-2xl hover:shadow-amber-500/10 transition-all duration-300 cursor-pointer">
              {/* Ambient effects */}
              <div className="absolute inset-0 overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/10 rounded-full blur-3xl" />
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-orange-500/10 rounded-full blur-3xl" />
              </div>
              
              <CardContent className="relative p-6 sm:p-8">
                <div className="flex flex-col sm:flex-row items-center gap-6">
                  {/* Microphone Visual */}
                  <div className="relative flex-shrink-0">
                    <div className="absolute inset-0 bg-amber-500/20 rounded-full blur-xl animate-pulse" />
                    <div className="relative w-24 h-24 sm:w-28 sm:h-28 bg-gradient-to-br from-slate-700 to-slate-800 rounded-full flex items-center justify-center border border-slate-600 shadow-xl">
                      <Mic className="w-12 h-12 sm:w-14 sm:h-14 text-amber-500" />
                    </div>
                    {/* Live indicator */}
                    <div className="absolute -top-1 -right-1 flex items-center gap-1 px-2 py-1 bg-slate-800 rounded-full border border-slate-700">
                      <Radio className="w-3 h-3 text-amber-400 animate-pulse" />
                      <span className="text-[10px] font-bold text-amber-400">STUDIO</span>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 text-center sm:text-left">
                    <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-500/10 border border-amber-500/20 rounded-full mb-3">
                      <Headphones className="w-3.5 h-3.5 text-amber-400" />
                      <span className="text-xs font-medium text-amber-400">17 ÉPISODES • +12H DE CONTENU</span>
                    </div>
                    <h3 className="text-2xl sm:text-3xl font-black text-white mb-2">
                      <span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">PODCAST</span>
                      <span className="text-white ml-2">STUDIO</span>
                    </h3>
                    <p className="text-slate-400 mb-4 max-w-md">
                      Les coulisses de l'investissement immobilier avec les experts du réseau 10M Investor
                    </p>
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-600 rounded-xl text-white font-medium shadow-lg shadow-amber-500/20 group-hover:shadow-amber-500/30 group-hover:scale-105 transition-all">
                      <Play className="w-4 h-4" />
                      Écouter maintenant
                    </div>
                  </div>
                  
                  {/* Stats on the right - hidden on mobile */}
                  <div className="hidden lg:flex flex-col gap-3">
                    <div className="flex items-center gap-3 px-4 py-2 bg-slate-800/50 rounded-xl border border-slate-700/50">
                      <div className="w-8 h-8 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                        <TrendingUp className="w-4 h-4 text-emerald-400" />
                      </div>
                      <div>
                        <div className="text-lg font-bold text-white">10</div>
                        <div className="text-[10px] text-slate-500">Disponibles</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 px-4 py-2 bg-slate-800/50 rounded-xl border border-slate-700/50">
                      <div className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center">
                        <Users className="w-4 h-4 text-purple-400" />
                      </div>
                      <div>
                        <div className="text-lg font-bold text-white">8</div>
                        <div className="text-[10px] text-slate-500">Thèmes</div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Club Events Widget */}
        <div className="mb-8">
          <Link to="/events">
            <Card className="group relative overflow-hidden bg-gradient-to-br from-indigo-950 via-slate-900 to-indigo-950 border-0 hover:shadow-2xl hover:shadow-blue-500/10 transition-all duration-300 cursor-pointer">
              {/* Ambient effects */}
              <div className="absolute inset-0 overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl" />
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-indigo-500/10 rounded-full blur-3xl" />
              </div>
              
              <CardContent className="relative p-6 sm:p-8">
                <div className="flex flex-col sm:flex-row items-center gap-6">
                  {/* Calendar Visual */}
                  <div className="relative flex-shrink-0">
                    <div className="absolute inset-0 bg-blue-500/20 rounded-2xl blur-xl animate-pulse" />
                    <div className="relative w-24 h-24 sm:w-28 sm:h-28 bg-gradient-to-br from-indigo-800 to-slate-900 rounded-2xl flex flex-col items-center justify-center border border-indigo-700 shadow-xl">
                      {upcomingEvents.length > 0 ? (
                        <>
                          <span className="text-xs font-bold text-blue-400">
                            {new Date(upcomingEvents[0].date).toLocaleDateString('fr-FR', { month: 'short' }).toUpperCase()}
                          </span>
                          <span className="text-4xl font-black text-white leading-none">
                            {new Date(upcomingEvents[0].date).getDate()}
                          </span>
                        </>
                      ) : (
                        <Calendar className="w-12 h-12 text-blue-500" />
                      )}
                    </div>
                    {/* VIP indicator */}
                    <div className="absolute -top-1 -right-1 flex items-center gap-1 px-2 py-1 bg-indigo-900 rounded-full border border-indigo-700">
                      <Star className="w-3 h-3 text-amber-400" />
                      <span className="text-[10px] font-bold text-blue-400">VIP</span>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 text-center sm:text-left">
                    <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-500/10 border border-blue-500/20 rounded-full mb-3">
                      <PartyPopper className="w-3.5 h-3.5 text-blue-400" />
                      <span className="text-xs font-medium text-blue-400">NETWORKING • MASTERCLASS • SOIRÉES</span>
                    </div>
                    <h3 className="text-2xl sm:text-3xl font-black text-white mb-2">
                      <span className="bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent">CLUB</span>
                      <span className="text-white ml-2">EVENTS</span>
                    </h3>
                    <p className="text-slate-400 mb-4 max-w-md">
                      Vivez des moments uniques avec la communauté des investisseurs 10M Investor
                    </p>
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl text-white font-medium shadow-lg shadow-blue-500/20 group-hover:shadow-blue-500/30 group-hover:scale-105 transition-all">
                      <Calendar className="w-4 h-4" />
                      Voir les événements
                    </div>
                  </div>
                  
                  {/* Stats on the right - hidden on mobile */}
                  <div className="hidden lg:flex flex-col gap-3">
                    <div className="flex items-center gap-3 px-4 py-2 bg-indigo-900/50 rounded-xl border border-indigo-700/50">
                      <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center">
                        <Calendar className="w-4 h-4 text-blue-400" />
                      </div>
                      <div>
                        <div className="text-lg font-bold text-white">{upcomingEvents.length}</div>
                        <div className="text-[10px] text-slate-500">À venir</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 px-4 py-2 bg-indigo-900/50 rounded-xl border border-indigo-700/50">
                      <div className="w-8 h-8 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                        <Users className="w-4 h-4 text-emerald-400" />
                      </div>
                      <div>
                        <div className="text-lg font-bold text-white">VIP</div>
                        <div className="text-[10px] text-slate-500">Exclusif</div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* New Features Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-amber-500" />
              Nouvelles fonctionnalités
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {newFeatures.map((feature, index) => (
              <Link key={index} to={feature.path}>
                <Card className="group border-slate-200 hover:shadow-xl transition-all cursor-pointer h-full overflow-hidden">
                  <div className={`h-2 bg-gradient-to-r ${feature.color}`} />
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      {feature.hasLogo ? (
                        <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${feature.color} flex items-center justify-center shadow-lg p-2`}>
                          <img src={logo10M} alt="10M Investor" className="w-full h-full object-contain" />
                        </div>
                      ) : (
                        <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${feature.color} flex items-center justify-center shadow-lg`}>
                          <feature.icon className="h-6 w-6 text-white" />
                        </div>
                      )}
                      <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                        feature.badge === 'Réseau' 
                          ? 'bg-violet-100 text-violet-700' 
                          : 'bg-amber-100 text-amber-700'
                      }`}>
                        {feature.badge}
                      </span>
                    </div>
                    <h3 className="font-bold text-lg text-slate-900 mb-2">{feature.label}</h3>
                    <p className="text-slate-600 text-sm mb-4">{feature.description}</p>
                    <div className="flex items-center text-[#0D47A1] font-medium text-sm group-hover:translate-x-1 transition-transform">
                      Découvrir <ArrowRight className="h-4 w-4 ml-1" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Recent Calculations */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Calculator className="h-5 w-5 mr-2 text-[#0D47A1]" />
                Derniers calculs
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats.calculations > 0 ? (
                <p className="text-slate-600">Vous avez effectué {stats.calculations} calcul(s).</p>
              ) : (
                <div className="text-center py-8">
                  <Calculator className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Aucun calcul pour le moment</p>
                  <Link to="/calculator">
                    <Button className="mt-4 bg-[#0D47A1] hover:bg-[#0A367A]" data-testid="start-calc-btn">
                      Faire mon premier calcul
                    </Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>

          {/* MDB Projects */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Building2 className="h-5 w-5 mr-2 text-[#00B050]" />
                Projets MDB
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats.mdbProjects > 0 ? (
                <Link to="/mdb" className="block">
                  <div className="p-4 bg-[#00B050]/5 rounded-lg hover:bg-[#00B050]/10 transition-colors cursor-pointer border border-[#00B050]/20">
                    <p className="text-slate-700 font-medium">
                      Vous avez <span className="text-[#00B050] font-bold">{stats.mdbProjects} projet(s)</span> MDB.
                    </p>
                    <p className="text-sm text-slate-500 mt-1">Cliquez pour voir et gérer vos projets →</p>
                  </div>
                </Link>
              ) : (
                <div className="text-center py-8">
                  <Building2 className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Aucun projet MDB pour le moment</p>
                  <Link to="/mdb">
                    <Button className="mt-4 bg-[#00B050] hover:bg-[#00853C]" data-testid="start-mdb-btn">
                      Créer un projet MDB
                    </Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default DashboardPage;
